import { isCalibrating } from '@studio/models/control/calibration';
import type { PTZ } from '@studio/models/ptz';
import { hasMovement } from '@studio/models/ptz';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import type { Props } from './types.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { DPad } from '#~/control/components/d-pad/index.js';
import { move } from '#~/control/movement/commands/index.js';
import { usePiston } from '#~/control/movement/hooks.js';
import { Section } from '#~/room/components/common/section/index.js';
import { useCalibration } from '#~/room/hooks.js';
import { locateProxy } from '#~/agent/utils.js';

/** `<Move>` component. */
export const Move = ({ deviceId, ...props }: Props): ReactElement => {
  const piston = usePiston(
    deviceId,
    useLatestFn((intent: PTZ) => {
      move(deviceId, intent, { toolUsed: 'dpad' });
    })
  );

  const bCalibrating = isCalibrating(useCalibration(deviceId));

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const handleMove = useLatestFn((intent: PTZ) => {
    if (bCalibrating) {
      return;
    }

    if (isProtected && hasMovement(intent)) {
      triggerControlAlert?.();

      return;
    }

    piston.process(intent);
  });

  const proxy = locateProxy(deviceId);

  const capabilities = proxy?.state.status?.device[deviceId]?.capabilities;

  const canPan = capabilities?.movement?.vectors?.pan?.enabled;
  const canTilt = capabilities?.movement?.vectors?.tilt?.enabled;

  return (
    <Section defaultOpen={false} slug='move' {...props}>
      <DPad
        deviceId={deviceId}
        allowZoom
        className='w-[190px]'
        disabled={bCalibrating}
        canPan={canPan}
        canTilt={canTilt}
        onMove={handleMove}
      />
    </Section>
  );
};
